/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgForm, AbstractControl } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from '../../services/user.service';
import { Constants } from '../../../common/constants';
import { XrxControlBaseComponent } from '../controls/xrx-control-base.component';
import { CountryCodeModalComponent } from './country-code-modal.component';

@Component({
  selector: 'xrx-xmfax',
  templateUrl: './xrx-xmfax.component.html',
  styleUrls: ['./xrx-xmfax.component.less'],
  providers: [
    { provide: XrxControlBaseComponent, useExisting: XrxXmFaxComponent },
  ],
  standalone: false,
})
export class XrxXmFaxComponent
  extends XrxControlBaseComponent
  implements OnInit
{
  @Input() required: boolean;
  @Input() label: string;
  @Input() value: string;
  @Input() countryCodes: any;
  @Input() isMfd: boolean;

  @ViewChild('f', { static: true }) public faxForm: NgForm;

  @Output() change = new EventEmitter<any>();
  @Output() validation = new EventEmitter<boolean>();
  @Output() focus = new EventEmitter<any>();
  @Output() blur = new EventEmitter<any>();

  isInvalid = false;
  cfg: faxConfig;
  language: any;
  _countryCodes: any;
  invalidCountryCode = false;
  invalidCountryCodeStr = '';
  invalidFaxNumber = false;
  invalidFaxNumberStr = '';
  faxNumberComboInvalid = false;
  invalidFaxNumberComboStr = '';
  subjectRequired = '';
  _blockedCodes: any = [];
  _selectedCountry: any;
  _selectedLanguage: any;
  maxlenCountryCode = 3;
  maxlenFaxNumber = 16;

  constructor(
    private modalService: NgbModal,
    public activeModal: NgbActiveModal,
    private userService: UserService,
  ) {
    super();
    this.cfg = {
      userName:
        userService?.user?.first_name + ' ' + userService?.user?.last_name,
      countryCode: '',
      faxNumber: '',
      subject: '',
      hasCoverPage: false,
      coverPageLanguage: '',
      name: '',
      company: '',
      senderName:
        userService.user?.first_name + ' ' + userService?.user?.last_name,
      senderCompany: userService?.user?.company || '',
      comments: '',
    };
  }

  ngOnInit() {
    this.language = Constants.languages;
    this.loadSettings();
    this.filterCountryCodeList();
    this.setCoverLanguage();
  }

  loadSettings() {
    if (this.value) {
      this.cfg = JSON.parse(this.value);

      this._selectedLanguage = this.language.find(
        (x: any) => x.ISOCode === this.cfg.coverPageLanguage,
      );
    }
  }

  filterCountryCodeList() {
    if (this.countryCodes) {
      this._countryCodes = this.countryCodes[1].options;
      // populate _blockedCodes array
      this.countryCodes[0].options.forEach((c: any) => {
        // don't filter out countries that should only have specific zip codes filtered
        if (!c.options) {
          this._blockedCodes.push(c.value);
        }
      });

      // filter out blocked country codes in countryCodes list
      this._countryCodes = this._countryCodes.filter(
        (c: any) => !this._blockedCodes.includes(c.value),
      );
    }
  }

  setCoverLanguage() {
    const userLang = this.userService?.user?.language;

    // don't overwrite selectedLangauge if it is already set (saved workflow, navigation back)
    if (!this.cfg.coverPageLanguage) {
      // find the language object in language and set coverPageLanguage based on userLang
      this._selectedLanguage = this.language.find(
        (l: any) => l.ISOCode == userLang,
      ) || {
        languageName: 'English',
        ISOCode: 'en-us',
        SDE_KEY: 'SDE_ENGLISH',
        nativeName: 'English',
        order: 5,
      };
      this.cfg.coverPageLanguage = this._selectedLanguage.ISOCode;
    }
  }

  /**
   * called by parent component
   */
  validate() {
    // validate country code
    if (!this.validateCountryCode()) {
      this.isInvalid = false;
      this.validation.emit(this.isInvalid);
      return;
    }

    // validate fax number
    if (!this.validateFaxNumber()) {
      this.isInvalid = false;
      this.validation.emit(this.isInvalid);
      return;
    }

    // validate full fax number (country code plus fax number)
    if (!this.validateFullFaxNumber()) {
      this.isInvalid = false;
      this.validation.emit(this.isInvalid);
      return;
    }

    // validate subject
    if (!this.validateSubject()) {
      this.isInvalid = false;
      this.validation.emit(this.isInvalid);
      return;
    }
  }

  onChange(e?: Event | boolean) {
    if (typeof e !== 'boolean' && e?.target) {
      e.stopPropagation();
    }

    // remove white space & dashes from fax #
    this.cfg.countryCode = this.cfg.countryCode.replace(/-|\s|/g, '');
    this.cfg.faxNumber = this.cfg.faxNumber.replace(/-|\s|/g, '');

    this.value = JSON.stringify(this.cfg);
    this.change.emit(this.value);
    this.validate();
  }

  onKeyUp(event: KeyboardEvent, input: string) {
    // ignore 'tab' key
    if (event.keyCode != 9) {
      // only clear the error if the error is not a maxlength error
      const control: AbstractControl | null = this.faxForm?.form?.get(input);
      if (!(control?.errors && control.errors['maxlength'])) {
        this.clearError(input);
      }

      if (input == 'CountryCode') {
        this.invalidCountryCode = false;
        this.faxNumberComboInvalid = false;
      } else if (input == 'FaxNumber') {
        this.invalidFaxNumber = false;
        this.faxNumberComboInvalid = false;
        // fix for double click for 'Next' button
        this.clearError('CountryCode');
        this.invalidCountryCode = false;
      }
    }
  }

  onFocus(e: boolean) {
    this.focus.emit(e);
  }

  onBlur(e: boolean) {
    this.blur.emit(e);
  }

  openCountryCodeModal() {
    const windowStyle = !this.isMfd ? 'xrx-modal' : 'xrx-popup';
    const windowClass = !this.isMfd ? 'modal-sm' : '';
    const countryCodeModal = this.modalService.open(CountryCodeModalComponent, {
      windowClass: windowStyle,
      modalDialogClass: windowClass,
    });
    countryCodeModal.componentInstance.countries = this._countryCodes;
    countryCodeModal.componentInstance.isMfd = this.isMfd;
    countryCodeModal.componentInstance.countryCode = this.cfg.countryCode;
    countryCodeModal.componentInstance.savedSelectedCountry =
      this._selectedCountry;
    countryCodeModal.result.then(
      (data: any) => {
        // update countryCode in cfg
        if (data) {
          this.cfg.countryCode = data.value;
          this._selectedCountry = data;
          this.onChange();
        }
      },
      () => {},
    );
  }

  validateCountryCode(): boolean {
    let isValid = true;

    // clear error string
    this.invalidCountryCodeStr = '';
    this.invalidFaxNumberComboStr = '';
    this.faxNumberComboInvalid = false;
    this.invalidCountryCode = false;
    this.clearError('CountryCode');

    if (this.cfg.countryCode == '') {
      setTimeout(() => {
        this.invalidFaxNumberComboStr = 'SDE_COUNTRY_CODE_NUMBER';
        this.faxNumberComboInvalid = true;
        this.setError('CountryCode');
      }, 100);
      isValid = false;
    } else if (
      !this.cfg.countryCode.match(/^\d+$/) ||
      parseInt(this.cfg.countryCode) == 0
    ) {
      setTimeout(() => {
        this.invalidCountryCodeStr = 'SDE_ENTER_COUNTRY_CODE';
        this.invalidCountryCode = true;
        this.setError('CountryCode');
      }, 100);
      isValid = false;
    } else if (this._blockedCodes.includes(this.cfg.countryCode)) {
      setTimeout(() => {
        this.invalidCountryCodeStr = 'SDE_COUNTRY_CODE_ENTERED';
        this.invalidCountryCode = true;
        this.setError('CountryCode');
      }, 100);
      isValid = false;
    } else if (
      !this._countryCodes.some((c: any) => c.value === this.cfg.countryCode)
    ) {
      setTimeout(() => {
        this.invalidCountryCodeStr = 'SDE_COUNTRY_CODE_ENTERED1';
        this.invalidCountryCode = true;
        this.setError('CountryCode');
      }, 100);
      isValid = false;
    }

    return isValid;
  }

  validateFaxNumber(): boolean {
    let isValid = true,
      cleanFaxNumber = this.cfg.faxNumber.replace(/-|\s|/g, '');

    // clear error string
    this.invalidFaxNumberStr = '';
    this.invalidFaxNumberComboStr = '';
    this.faxNumberComboInvalid = false;
    this.invalidFaxNumber = false;
    this.clearError('FaxNumber');

    if (cleanFaxNumber == '') {
      setTimeout(() => {
        this.invalidFaxNumberComboStr = 'SDE_COUNTRY_CODE_NUMBER';
        this.faxNumberComboInvalid = true;
        this.setError('FaxNumber');
      }, 100);
      isValid = false;
    } else if (cleanFaxNumber.length < 7) {
      setTimeout(() => {
        this.invalidFaxNumberStr = 'SDE_NUMBER_INVALID_FORMAT';
        this.invalidFaxNumber = true;
        this.setError('FaxNumber');
      }, 100);
      isValid = false;
    } else if (/\D/g.test(cleanFaxNumber)) {
      setTimeout(() => {
        this.invalidFaxNumberStr = 'SDE_NUMBER_INVALID_FORMAT';
        this.invalidFaxNumber = true;
        this.setError('FaxNumber');
      }, 100);
      isValid = false;
    }

    return isValid;
  }

  validateFullFaxNumber(): boolean {
    let isValid = true,
      cleanFaxNumber = this.cfg.faxNumber.replace(/-|\s|/g, ''),
      zipCodes = this.countryCodes[0].options.find(
        (c: any) => c.value === this.cfg.countryCode && c.options,
      );

    // clear errors
    this.invalidCountryCodeStr = '';
    this.invalidCountryCode = false;
    this.clearError('CountryCode');

    if (
      zipCodes &&
      zipCodes.options.some((z: any) =>
        cleanFaxNumber.startsWith(String(z.value)) ? true : false,
      )
    ) {
      setTimeout(() => {
        this.invalidCountryCodeStr = 'SDE_COUNTRY_CODE_ENTERED';
        this.invalidCountryCode = true;
        this.setError('CountryCode');
      }, 100);
      isValid = false;
    } else {
      isValid = true;
    }
    return isValid;
  }

  validateSubject(): boolean {
    let isValid = true;

    // clear errors
    this.subjectRequired = '';
    this.clearError('Subject');

    if (this.cfg.hasCoverPage && this.cfg.subject == '') {
      setTimeout(() => {
        this.subjectRequired = 'SDE_REQUIRED_FIELD1';
        this.setError('Subject');
      }, 100);
      isValid = false;
    }
    return isValid;
  }

  setError(control: string) {
    const ctrl = this.faxForm.form.get(control);
    if (ctrl) {
      setTimeout(() => {
        ctrl.setErrors({ customerror: true });
        ctrl.markAsTouched();
        ctrl.markAsDirty();
      }, 100);
    }
  }

  clearError(control: string) {
    const ctrl = this.faxForm.form.get(control);
    this.faxNumberComboInvalid = false;
    if (ctrl) {
      ctrl.setErrors({});
      ctrl.markAsPristine();
      ctrl.updateValueAndValidity();
    }
  }
}

interface faxConfig {
  userName: string;
  countryCode: string;
  faxNumber: string;
  subject: string;
  hasCoverPage: boolean;
  coverPageLanguage: string;
  name: string;
  company: string;
  senderName: string;
  senderCompany: string;
  comments: string;
}
